'use client';

import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/core/presentation/components/ui/Button';
import { CustomSelect } from '@/core/presentation/components/ui/CustomSelect';
import { AppTabsToolbar, appTabsSectionContentClassName } from '@/core/presentation/components/ui/AppTabsToolbar';
import { Tabs, TabsContent, TabsTrigger } from '@/core/presentation/components/ui/Tabs';
import AppIcon from '@/core/presentation/components/ui/AppIcon';
import { SectionScreenLayout } from '@/features/app/presentation/components/SectionScreenLayout';
import { LEMBRETES_CLIENT_FALLBACK } from '@/features/app/presentation/constants/lembretes-calendario-copy';
import { useClients } from '@/features/app/clients/presentation/context/ClientContext';
import { useReminders } from '@/features/app/reminders/presentation/context/ReminderContext';
import { useServices } from '@/features/app/services/presentation/context/ServiceContext';
import {
  DASHBOARD_LOADING,
  DASHBOARD_REGISTER_SERVICE_BUTTON,
  DASHBOARD_SECTION_SUBTITLE,
  DASHBOARD_SECTION_TITLE,
  DASHBOARD_TAB_METRICS,
  DASHBOARD_TAB_OVERVIEW,
  DASHBOARD_TABLIST_ARIA_LABEL,
  DASHBOARD_YEAR_SELECT_ARIA,
} from '@/features/app/dashboard/presentation/constants/dashboard-copy';
import { dashboardYearSelectOptions } from '@/features/app/dashboard/presentation/utils/dashboard-year-select-options';
import { buildDashboardMockDataset } from '@/features/app/dashboard/presentation/utils/dashboard-mock-dataset';
import { DashboardOverviewTab } from '@/features/app/dashboard/presentation/components/DashboardOverviewTab';
import { useAwaitInitialFetch } from '@/features/app/presentation/hooks/use-await-initial-fetch';

const DashboardYearMetricsTab = lazy(async () => {
  const m = await import('@/features/app/dashboard/presentation/components/DashboardYearMetricsTab');
  return { default: m.DashboardYearMetricsTab };
});

export type DashboardTabId = 'overview' | 'charts';

const TAB_OVERVIEW: DashboardTabId = 'overview';
const TAB_METRICS: DashboardTabId = 'charts';

const DASHBOARD_USE_MOCK_DATA = import.meta.env.VITE_DASHBOARD_USE_MOCK_DATA === 'true';

export interface DashboardSectionProps {
  onRegisterServiceClick?: () => void;
}

export const DashboardSection: React.FC<DashboardSectionProps> = ({ onRegisterServiceClick }) => {
  const { state: reminderState, events: reminderEvents } = useReminders();
  const { state: clientState, events: clientEvents } = useClients();
  const { state: serviceState, events: serviceEvents } = useServices();

  const mockDataset = useMemo(
    () => (DASHBOARD_USE_MOCK_DATA ? buildDashboardMockDataset(new Date()) : null),
    []
  );

  const [activeTab, setActiveTab] = useState<DashboardTabId>(TAB_OVERVIEW);
  const [metricsYear, setMetricsYear] = useState(() => String(new Date().getFullYear()));
  const [showFloatingButton, setShowFloatingButton] = useState(true);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  const loadDashboardData = useCallback(async () => {
    if (DASHBOARD_USE_MOCK_DATA) return;
    await Promise.all([
      reminderEvents.getByUserId(),
      clientEvents.getByUserId(),
      serviceEvents.getByUserId(),
    ]);
  }, [reminderEvents.getByUserId, clientEvents.getByUserId, serviceEvents.getByUserId]);

  const dashboardDataReady = useAwaitInitialFetch(loadDashboardData, [loadDashboardData]);

  useEffect(() => {
    const handleScroll = () => {
      if (ticking.current) return;
      ticking.current = true;
      requestAnimationFrame(() => {
        const scrollY = window.scrollY ?? document.documentElement.scrollTop;
        const delta = scrollY - lastScrollY.current;
        if (Math.abs(delta) > 30) {
          if (delta > 0) {
            setShowFloatingButton(false);
          } else {
            setShowFloatingButton(true);
          }
        }
        lastScrollY.current = scrollY;
        ticking.current = false;
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const activeReminders = useMemo(() => {
    if (mockDataset) {
      return mockDataset.reminders.filter((r) => r.status !== 'cancelled');
    }
    return reminderState.reminders.filter((r) => r.status !== 'cancelled');
  }, [mockDataset, reminderState.reminders]);

  const getClientName = useCallback(
    (clientId: string): string => {
      if (mockDataset) {
        const client = mockDataset.clients.find((c) => c.id === clientId);
        return client?.name ?? LEMBRETES_CLIENT_FALLBACK;
      }
      const client = clientState.clients.find((c) => c.id === clientId);
      return client?.name ?? LEMBRETES_CLIENT_FALLBACK;
    },
    [clientState.clients, mockDataset]
  );

  const listLoading = !DASHBOARD_USE_MOCK_DATA && !dashboardDataReady;

  const dashboardError = DASHBOARD_USE_MOCK_DATA ? null : reminderState.error;

  const metricsServices = mockDataset?.services ?? serviceState.services;

  const yearOptions = useMemo(() => dashboardYearSelectOptions(), []);

  const metricsTabActive = activeTab === TAB_METRICS;

  const metricsYearNumber = useMemo(() => {
    const n = Number.parseInt(metricsYear, 10);
    return Number.isFinite(n) ? n : new Date().getFullYear();
  }, [metricsYear]);

  return (
    <SectionScreenLayout title={DASHBOARD_SECTION_TITLE} subtitle={DASHBOARD_SECTION_SUBTITLE}>
      <div className="space-y-4 min-w-0 overflow-hidden">
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as DashboardTabId)}
          className="w-full min-w-0"
        >
          <AppTabsToolbar
            listAriaLabel={DASHBOARD_TABLIST_ARIA_LABEL}
            splitWhen={metricsTabActive}
            end={
              metricsTabActive ?
                <CustomSelect
                  id="dashboard-year-ref"
                  variant="toolbar"
                  menuAlign="end"
                  leadingIconName="CalendarDaysIcon"
                  ariaLabel={DASHBOARD_YEAR_SELECT_ARIA}
                  options={yearOptions}
                  value={metricsYear}
                  onChange={setMetricsYear}
                  disabled={listLoading}
                />
              : null
            }
          >
            <TabsTrigger value={TAB_OVERVIEW}>{DASHBOARD_TAB_OVERVIEW}</TabsTrigger>
            <TabsTrigger value={TAB_METRICS}>{DASHBOARD_TAB_METRICS}</TabsTrigger>
          </AppTabsToolbar>

          {dashboardError ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              {dashboardError}
            </div>
          ) : null}

          {listLoading ? (
            <div className="flex items-center justify-center py-16">
              <p className="text-text-muted text-sm">{DASHBOARD_LOADING}</p>
            </div>
          ) : (
            <>
              <TabsContent value={TAB_OVERVIEW} className={appTabsSectionContentClassName()}>
                <DashboardOverviewTab
                  reminders={activeReminders}
                  getClientName={getClientName}
                  suppressSendFailureAlerts={DASHBOARD_USE_MOCK_DATA}
                />
              </TabsContent>
              <TabsContent value={TAB_METRICS} className={appTabsSectionContentClassName()}>
                <Suspense
                  fallback={
                    <div className="flex items-center justify-center py-16">
                      <p className="text-text-muted text-sm">{DASHBOARD_LOADING}</p>
                    </div>
                  }
                >
                  <DashboardYearMetricsTab
                    year={metricsYearNumber}
                    reminders={activeReminders}
                    services={metricsServices}
                  />
                </Suspense>
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>

      {onRegisterServiceClick ? <div className="h-16 sm:h-20" aria-hidden /> : null}

      {onRegisterServiceClick ? (
        <div
          className={`fixed z-40 transition-all duration-300 ease-out bottom-[calc(1.5rem+env(safe-area-inset-bottom,0px))] right-[calc(1.5rem+env(safe-area-inset-right,0px))] ${
            showFloatingButton
              ? 'opacity-100 translate-y-0 pointer-events-auto'
              : 'opacity-0 translate-y-4 pointer-events-none'
          }`}
        >
          <Button type="button" size="lg" className="gap-2 shadow-lg" onClick={onRegisterServiceClick}>
            <AppIcon name="PlusCircleIcon" size={22} />
            {DASHBOARD_REGISTER_SERVICE_BUTTON}
          </Button>
        </div>
      ) : null}
    </SectionScreenLayout>
  );
};
